// Implements: F-USG-006
/**
 * The caller's share of one mockup's cost, for Mi consumo's breakdown (owner 2026-10-19).
 *
 * The answer is the signed-in person's own (whose runs are theirs), so the key carries the
 * person, like every read of My usage (`myUsageKey`): a second person signing in on the same tab
 * never reads the first one's share, and an answer in flight for one is never shown to the other
 * (checklist 14). It carries the mockup and the asked ids too, so one mockup's answer never fills
 * another's. A refusal is read by the container (`isCostRefusal`), never retried by hand.
 *
 * A run the server has not counted yet (the reporting projection runs after the run closed) is
 * asked again every `PENDING_REFETCH_MS` while it is pending, and never once all are counted, so
 * "Pendiente" turns into the caller's share without a reload (verifier, lane 28).
 */

import { useQuery } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";

import type { ApiError } from "../../../shared/api/ApiError";
import type { CostSplitView } from "../../../types/costs";
import { fetchCostSplit } from "../api/costSplitApi";

/** How soon a pending run is asked again. */
export const PENDING_REFETCH_MS = 10_000;

/** The refetch interval of a split: soon while an asked run is pending, never otherwise. */
export function refetchWhilePending(view: CostSplitView | undefined, asked: readonly string[]): number | false {
  if (view === undefined) return false;
  const known = new Set(view.known_generation_ids);
  return asked.some((id) => !known.has(id)) ? PENDING_REFETCH_MS : false;
}

export function costSplitKey(
  personId: string,
  mockupId: string,
  generationIds: readonly string[],
): readonly unknown[] {
  return ["backoffice", "me", personId, "cost-split", mockupId, ...generationIds];
}

export function useCostSplit(
  personId: string,
  mockupId: string,
  generationIds: readonly string[],
  enabled: boolean,
): UseQueryResult<CostSplitView, ApiError> {
  return useQuery<CostSplitView, ApiError>({
    queryKey: costSplitKey(personId, mockupId, generationIds),
    queryFn: ({ signal }) => fetchCostSplit(mockupId, generationIds, signal),
    enabled,
    refetchInterval: (query) => refetchWhilePending(query.state.data, generationIds),
  });
}
